import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CURRENT_API_YEAR } from '@/lib/season';
import { getInformalName } from '@/lib/teams';
import { useLeagueConfig } from '@/hooks/useLeagueConfig';

interface TeamStanding {
  id: number;
  name: string;
  logo: string;
  position: number;
  points: number;
  wins?: number;
  draws?: number;
  losses?: number;
}

export interface TeamSeasonRecord {
  position: number;
  points: number;
  wins: number;
  draws: number;
  losses: number;
}

interface StandingsResponse {
  season: string;
  tddIneligibleTeams: TeamStanding[];
  tddEligibleTeams: TeamStanding[];
  lastUpdated: string;
}

/**
 * Hook to fetch current season standings and return team ranks
 * Returns a map of team name (informal) -> position
 */
export const useCurrentSeasonStandings = (season: string = CURRENT_API_YEAR) => {
  const { competitionId } = useLeagueConfig();

  return useQuery({
    queryKey: ['current-season-standings', season, competitionId],
    enabled: competitionId != null,
    queryFn: async () => {
      console.log(`Fetching current season standings for ${season} (competition ${competitionId})`);

      const { data, error } = await supabase.functions.invoke('fetch-standings', {
        body: { season, competitionId }
      });

      if (error) {
        console.error('Error fetching current season standings:', error);
        throw error;
      }

      const response = data as StandingsResponse;
      
      // Build maps for team data
      const teamRanks: { [teamName: string]: number } = {};
      const teamRecords: { [teamName: string]: TeamSeasonRecord } = {};
      
      // Process all teams from both lists
      const allTeams = [
        ...(response.tddIneligibleTeams || []),
        ...(response.tddEligibleTeams || [])
      ];
      
      for (const team of allTeams) {
        if (team.position) {
          // Store by both formal and informal names for easier lookup
          teamRanks[team.name] = team.position;
          teamRanks[getInformalName(team.name)] = team.position;
          
          const record: TeamSeasonRecord = {
            position: team.position,
            points: team.points ?? 0,
            wins: team.wins ?? 0,
            draws: team.draws ?? 0,
            losses: team.losses ?? 0,
          };
          teamRecords[team.name] = record;
          teamRecords[getInformalName(team.name)] = record;
        }
      }
      
      return {
        teamRanks,
        teamRecords,
        lastUpdated: response.lastUpdated,
        season: response.season
      };
    },
    staleTime: 6 * 60 * 60 * 1000, // 6 hours - standings change during matchdays
    retry: 2,
    retryDelay: 1000,
  });
};

